/**
 * Query-string contract for ``widget.html``.
 *
 * The loader builds the iframe URL with ``tenant`` + ``chatbot``;
 * ``mode=app`` is for direct (non-loader) embeds — e.g. a mobile-app
 * webview pointing straight at ``/embed/v1/widget.html``. App mode
 * boots the panel open + fullscreen and removes the close /
 * fullscreen-toggle chrome, so the chat *is* the screen and can't be
 * minimized.
 */
export interface WidgetParams {
  tenant: string | null;
  chatbot: string | null;
  /** True when ``mode=app`` — always-open, always-fullscreen embed. */
  appMode: boolean;
}

const EMPTY_PARAMS: WidgetParams = {
  tenant: null,
  chatbot: null,
  appMode: false,
};

/**
 * Parse widget params from ``href`` (defaults to the current page).
 * Returns empty params when no URL is available (SSR) or the href is
 * malformed.
 */
export function readParams(href?: string): WidgetParams {
  const source =
    href ?? (typeof window === "undefined" ? null : window.location.href);
  if (!source) return EMPTY_PARAMS;
  let url: URL;
  try {
    url = new URL(source);
  } catch {
    return EMPTY_PARAMS;
  }
  return {
    tenant: url.searchParams.get("tenant"),
    chatbot: url.searchParams.get("chatbot"),
    appMode: url.searchParams.get("mode") === "app",
  };
}
